/**
 * Employee management — admin-only page for inviting + removing teammates.
 *
 * Two-pane:
 *   • Left: invite form (email, display name, role)
 *   • Right: list of all members with their status + a Remove action.
 *
 * Remove uses soft-delete on the membership row server-side. Guardrails: you
 * can't remove yourself, and the last admin can't be removed (the backend
 * enforces both — the UI also disables the button to keep the affordance
 * honest).
 *
 * On invite-success the backend returns a one-time activation URL. We surface
 * it in a copy-to-clipboard banner so the admin can paste it into Slack /
 * email until email delivery is fully wired up.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";

import { useIsMobile } from "@/hooks/useIsMobile";
import { api, extractErrorMessage } from "@/lib/api";
import type { Member } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

type InviteResponse = {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  job_id: string | null;
  designation: string | null;
  activation_url: string;
};

const ADMIN_ROLES = new Set(["admin"]);
type Role = "admin" | "employee";

export function OnboardingPage() {
  const memberships = useAuthStore((s) => s.memberships);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));
  const isMobile = useIsMobile();

  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("employee");
  const [jobId, setJobId] = useState("");
  const [designation, setDesignation] = useState("");
  const [lastInvite, setLastInvite] = useState<InviteResponse | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Member | null>(null);
  const [editJobId, setEditJobId] = useState("");
  const [editDesignation, setEditDesignation] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: async () => (await api.get<Member[]>("/admin/members")).data,
    enabled: isAdmin,
  });

  const invite = useMutation({
    mutationFn: async () => {
      const body = {
        email: email.trim(),
        display_name: displayName.trim(),
        role,
        job_id: jobId.trim() || null,
        designation: designation.trim() || null,
      };
      return (await api.post<InviteResponse>("/admin/members", body)).data;
    },
    onSuccess: (resp) => {
      setLastInvite(resp);
      setBannerError(null);
      setEmail("");
      setDisplayName("");
      setRole("employee");
      setJobId("");
      setDesignation("");
      setCopied(false);
      qc.invalidateQueries({ queryKey: ["members"] });
    },
    onError: (err) => {
      setBannerError(extractErrorMessage(err, "Could not send invite."));
    },
  });

  const remove = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/admin/members/${userId}`);
    },
    onSuccess: () => {
      setRemoveTarget(null);
      setRemoveError(null);
      qc.invalidateQueries({ queryKey: ["members"] });
    },
    onError: (err) => {
      setRemoveError(extractErrorMessage(err, "Could not remove this member."));
    },
  });

  const edit = useMutation({
    mutationFn: async (vars: { userId: string; job_id: string; designation: string }) =>
      (
        await api.patch<Member>(`/admin/members/${vars.userId}`, {
          job_id: vars.job_id,
          designation: vars.designation,
        })
      ).data,
    onSuccess: () => {
      setEditTarget(null);
      setEditError(null);
      qc.invalidateQueries({ queryKey: ["members"] });
    },
    onError: (err) => {
      setEditError(extractErrorMessage(err, "Could not save changes."));
    },
  });

  // Count active admins so we can disable Remove on the only one.
  const adminCount = members.filter((m) => m.role === "admin").length;

  if (!isAdmin) {
    return (
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px" }}>
          Employee management
        </h1>
        <p style={{ color: "var(--mute)", marginTop: 8 }}>
          Admin-only. Ask your workspace admin to invite or remove teammates here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Employee management
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Invite teammates, and remove anyone who's left. Removals are reversible until purged.
        </p>
      </header>

      {/* Invite-success banner with copy-to-clipboard activation URL */}
      {lastInvite && (
        <div style={successBanner}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Invited {lastInvite.display_name} as {lastInvite.role}.
          </div>
          <div style={{ fontSize: 13, color: "var(--body)" }}>
            Send them this activation link — expires in 24 hours.
          </div>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <input
              readOnly
              value={lastInvite.activation_url}
              style={{
                flex: 1,
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                padding: "6px 10px",
                background: "var(--canvas)",
                border: "1px solid var(--hairline)",
                borderRadius: 4,
                color: "var(--ink)",
              }}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(lastInvite.activation_url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              style={primaryBtn}
            >
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          // Two-pane on desktop (form left, member list right); single
          // column on mobile so each pane gets the full viewport.
          gridTemplateColumns: isMobile ? "1fr" : "380px 1fr",
          gap: isMobile ? 16 : 24,
        }}
      >
        {/* Invite form */}
        <section style={card}>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>
            Invite a teammate
          </h2>
          <label style={fieldRow}>
            <span style={fieldLabel}>Email</span>
            <input
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={input}
              autoComplete="off"
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Display name</span>
            <input
              type="text"
              placeholder="Sara Lee"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={input}
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              style={input}
            >
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Job ID (optional)</span>
            <input
              type="text"
              placeholder="EMP-001"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              style={{ ...input, fontFamily: "var(--font-mono)" }}
              maxLength={64}
              autoComplete="off"
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Designation (optional)</span>
            <input
              type="text"
              placeholder="Business analyst"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              style={input}
              maxLength={128}
              autoComplete="off"
            />
          </label>

          {bannerError && (
            <p
              role="alert"
              style={{
                marginTop: 12,
                padding: "8px 12px",
                fontSize: 13,
                color: "#ee1d36",
                background: "rgba(238,29,54,0.06)",
                border: "1px solid rgba(238,29,54,0.3)",
                borderRadius: 4,
              }}
            >
              {bannerError}
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              setBannerError(null);
              if (!email.trim() || !displayName.trim()) {
                setBannerError("Email and display name are required.");
                return;
              }
              invite.mutate();
            }}
            disabled={invite.isPending}
            style={{
              ...primaryBtn,
              marginTop: 16,
              width: "100%",
              opacity: invite.isPending ? 0.6 : 1,
              cursor: invite.isPending ? "wait" : "pointer",
            }}
          >
            {invite.isPending ? "Sending invite…" : "Send invite"}
          </button>
        </section>

        {/* Member list */}
        <section>
          <div style={sectionTitle}>
            Members · {isLoading ? "…" : members.length}
          </div>
          {isLoading ? (
            <p style={{ color: "var(--mute)" }}>Loading…</p>
          ) : members.length === 0 ? (
            <p style={{ color: "var(--mute)" }}>No members yet.</p>
          ) : (
            <div
              style={{
                border: "1px solid var(--hairline)",
                borderRadius: 8,
                background: "var(--canvas)",
                // Horizontal scroll on narrow viewports so the 6-column
                // table doesn't crush. Visible scrollbar is the affordance.
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  minWidth: 720,
                }}
              >
                <thead>
                  <tr style={{ background: "var(--surface-1)" }}>
                    <th style={th}>Name</th>
                    <th style={th}>Email</th>
                    <th style={th}>Job ID</th>
                    <th style={th}>Designation</th>
                    <th style={th}>Role</th>
                    <th style={th}>Status</th>
                    <th style={th}>Last seen</th>
                    <th style={{ ...th, width: 160 }} />
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const isSelf = currentUserId === m.user_id;
                    const isLastAdmin = m.role === "admin" && adminCount <= 1;
                    const disabled = isSelf || isLastAdmin;
                    const reason = isSelf
                      ? "You can't remove yourself"
                      : isLastAdmin
                        ? "Last admin — promote someone else first"
                        : "";
                    return (
                      <tr key={m.user_id} style={{ borderTop: "1px solid var(--hairline)" }}>
                        <td style={td}>{m.display_name}</td>
                        <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12 }}>
                          {m.email}
                        </td>
                        <td
                          style={{
                            ...td,
                            fontFamily: "var(--font-mono)",
                            fontSize: 12,
                            color: m.job_id ? "var(--ink)" : "var(--mute)",
                          }}
                        >
                          {m.job_id ?? "—"}
                        </td>
                        <td
                          style={{
                            ...td,
                            color: m.designation ? "var(--ink)" : "var(--mute)",
                          }}
                        >
                          {m.designation ?? "—"}
                        </td>
                        <td style={td}>
                          <span style={rolePill(m.role)}>{m.role}</span>
                        </td>
                        <td style={td}>
                          <span style={statusPill(m.status)}>{m.status ?? "active"}</span>
                        </td>
                        <td style={{ ...td, color: "var(--mute)" }}>
                          {m.last_seen_at
                            ? new Date(m.last_seen_at).toLocaleDateString()
                            : "—"}
                        </td>
                        <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            onClick={() => {
                              setEditTarget(m);
                              setEditJobId(m.job_id ?? "");
                              setEditDesignation(m.designation ?? "");
                              setEditError(null);
                            }}
                            title={`Edit ${m.display_name}`}
                            style={editMemberBtn}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRemoveError(null);
                              setRemoveTarget(m);
                            }}
                            disabled={disabled}
                            title={reason || `Remove ${m.display_name}`}
                            style={{ ...removeBtn(disabled), marginLeft: 6 }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {removeTarget && (
        <ConfirmRemoveDialog
          member={removeTarget}
          pending={remove.isPending}
          error={removeError}
          onCancel={() => {
            if (remove.isPending) return;
            setRemoveTarget(null);
            setRemoveError(null);
          }}
          onConfirm={() => remove.mutate(removeTarget.user_id)}
        />
      )}

      {editTarget && (
        <EditMemberDialog
          member={editTarget}
          jobId={editJobId}
          designation={editDesignation}
          onJobIdChange={setEditJobId}
          onDesignationChange={setEditDesignation}
          pending={edit.isPending}
          error={editError}
          onCancel={() => {
            if (edit.isPending) return;
            setEditTarget(null);
            setEditError(null);
          }}
          onConfirm={() =>
            edit.mutate({
              userId: editTarget.user_id,
              job_id: editJobId,
              designation: editDesignation,
            })
          }
        />
      )}
    </div>
  );
}

// ---------- edit member dialog ----------

function EditMemberDialog({
  member,
  jobId,
  designation,
  onJobIdChange,
  onDesignationChange,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  member: Member;
  jobId: string;
  designation: string;
  onJobIdChange: (s: string) => void;
  onDesignationChange: (s: string) => void;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div role="dialog" aria-modal="true" style={overlay} onClick={onCancel}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: "0 0 6px" }}>
          Edit {member.display_name}
        </h2>
        <p style={{ color: "var(--mute)", fontSize: 13, margin: "0 0 16px" }}>
          {member.email}
        </p>
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={editFieldLabel}>Job ID</span>
          <input
            type="text"
            value={jobId}
            onChange={(e) => onJobIdChange(e.target.value)}
            placeholder="EMP-001"
            style={{ ...editInput, fontFamily: "var(--font-mono)" }}
            maxLength={64}
            autoComplete="off"
          />
        </label>
        <label style={{ display: "block", marginBottom: 16 }}>
          <span style={editFieldLabel}>Designation</span>
          <input
            type="text"
            value={designation}
            onChange={(e) => onDesignationChange(e.target.value)}
            placeholder="Business analyst"
            style={editInput}
            maxLength={128}
            autoComplete="off"
          />
        </label>
        {error && (
          <p
            role="alert"
            style={{
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
              padding: "8px 12px",
              margin: "0 0 12px",
            }}
          >
            {error}
          </p>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} disabled={pending} style={secondaryBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            style={{
              ...primaryBtn,
              opacity: pending ? 0.6 : 1,
              cursor: pending ? "wait" : "pointer",
            }}
          >
            {pending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- confirm dialog ----------

function ConfirmRemoveDialog({
  member,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  member: Member;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div role="dialog" aria-modal="true" style={overlay} onClick={onCancel}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: "0 0 6px" }}>
          Remove {member.display_name}?
        </h2>
        <p style={{ color: "var(--mute)", fontSize: 13, margin: "0 0 16px", lineHeight: 1.55 }}>
          They'll lose access to this workspace immediately. Their authored issues, comments, and pages stay attributed to them. This is reversible until the data is purged.
        </p>
        {error && (
          <p
            role="alert"
            style={{
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
              padding: "8px 12px",
              margin: "0 0 12px",
            }}
          >
            {error}
          </p>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} disabled={pending} style={secondaryBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            style={{ ...dangerBtn, opacity: pending ? 0.6 : 1, cursor: pending ? "wait" : "pointer" }}
          >
            {pending ? "Removing…" : "Remove member"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- styles ----------

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 12,
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const secondaryBtn: CSSProperties = {
  background: "transparent",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const dangerBtn: CSSProperties = {
  background: "#c11930",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const editMemberBtn: CSSProperties = {
  background: "transparent",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
};

const editFieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const editInput: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontSize: 13,
  color: "var(--ink)",
  outline: "none",
  boxSizing: "border-box",
};

function removeBtn(disabled: boolean): CSSProperties {
  return {
    background: "transparent",
    color: disabled ? "var(--mute)" : "#c11930",
    border: `1px solid ${disabled ? "var(--hairline)" : "rgba(193,25,48,0.35)"}`,
    borderRadius: 4,
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 500,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

const successBanner: CSSProperties = {
  background: "rgba(0,215,34,0.06)",
  border: "1px solid rgba(0,215,34,0.25)",
  borderRadius: 6,
  padding: "12px 16px",
  marginBottom: 16,
  fontSize: 13,
  color: "var(--ink)",
};

const th: CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  padding: "10px 14px",
  borderBottom: "1px solid var(--hairline)",
};

const td: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13,
  color: "var(--body)",
};

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(8,8,8,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
};

const modal: CSSProperties = {
  width: "min(440px, calc(100vw - 32px))",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
  boxShadow: "0 12px 32px rgba(8,8,8,0.18)",
};

function rolePill(role: string): CSSProperties {
  const privileged = role === "admin";
  return {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    padding: "2px 8px",
    borderRadius: 3,
    background: privileged ? "var(--ink)" : "var(--surface-2)",
    color: privileged ? "var(--on-primary)" : "var(--body-mid)",
  };
}

function statusPill(status: string): CSSProperties {
  const isActive = status === "active";
  return {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    padding: "2px 8px",
    borderRadius: 3,
    background: isActive ? "rgba(0,215,34,0.12)" : "rgba(255,174,19,0.14)",
    color: isActive ? "#047a18" : "#8a5b00",
  };
}
